import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, AlertTriangle, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewReport = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [inspection, setInspection] = useState(null);

  useEffect(() => {
    fetchData();
  }, [bookingId]);

  const fetchData = async () => {
    try {
      const [bookingRes, inspectionRes] = await Promise.all([
        axios.get(`${API}/bookings/${bookingId}`),
        axios.get(`${API}/inspections/booking/${bookingId}`)
      ]);
      
      setBooking(bookingRes.data);
      setInspection(inspectionRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load inspection report');
    } finally {
      setLoading(false);
    }
  };

  const renderCheckItem = (label, check) => {
    if (!check) return null;
    
    return (
      <div className="border-b border-zinc-700 py-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <p className="font-semibold text-white uppercase text-sm">{label}</p>
            {check.notes && (
              <p className="text-red-400 text-sm mt-1">{check.notes}</p>
            )}
          </div>
          <div>
            {check.status === 'good' ? (
              <CheckCircle className="w-5 h-5 text-green-500" data-testid={`check-good-${label}`} />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500" data-testid={`check-issue-${label}`} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title, checks) => {
    if (!checks) return null;
    
    const checkEntries = Object.entries(checks);
    
    return (
      <div className="mb-8">
        <div className="bg-zinc-800 px-6 py-3 mb-2">
          <h3 className="text-xl font-bold text-white uppercase tracking-wide">{title}</h3>
        </div>
        <div className="px-6">
          {checkEntries.map(([key, check]) => 
            renderCheckItem(key.replace(/([A-Z])/g, ' $1').trim(), check)
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
        <p className="text-white">Loading report...</p>
      </div>
    );
  }

  if (!inspection || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
        <div className="text-center">
          <p className="text-white mb-4">Report not found</p>
          <Button onClick={() => navigate('/admin')} data-testid="back-to-admin-btn">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            className="text-zinc-300 hover:text-white hover:bg-white/10"
            onClick={() => navigate('/admin')}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-white/5"
            onClick={() => window.print()}
            data-testid="print-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download / Print
          </Button>
        </div>
      </header>

      {/* Report Content */}
      <div className="max-w-5xl mx-auto px-6 py-12 bg-white my-8 shadow-lg" id="report-content">
        {/* Logo & Title */}
        <div className="text-center mb-8 border-b-4 border-zinc-900 pb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/advll02n_ChatGPT%20Image%20Jul%2011%2C%202026%2C%2004_01_30%20PM.png" 
            alt="CarLens" 
            className="h-24 mx-auto mb-4"
          />
        </div>

        {/* Vehicle Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-zinc-900 mb-2" data-testid="report-vehicle-title">
            {booking.carYear} {booking.carMake} {booking.carModel}
          </h1>
          <p className="text-zinc-600">
            Date of Inspection: {new Date(inspection.createdAt).toLocaleDateString()}
          </p>
        </div>

        {/* Photo Gallery */}
        {inspection.photos && inspection.photos.length > 0 && (
          <div className="mb-8">
            <div className="border-4 border-orange-400 p-2">
              <img 
                src={inspection.photos[0]} 
                alt="Vehicle" 
                className="w-full h-96 object-cover"
              />
            </div>
            <div className="text-center mt-4">
              <button className="bg-orange-400 text-white px-6 py-2 font-bold">
                Click to View Large Gallery
              </button>
            </div>
          </div>
        )}

        {/* Summary */}
        {inspection.summary && (
          <div className="mb-8 bg-zinc-100 p-6">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">SUMMARY</h2>
            <p className="text-zinc-800 leading-relaxed" data-testid="inspection-summary">
              {inspection.summary}
            </p>
          </div>
        )}

        {/* Vehicle Info Table */}
        <div className="mb-8">
          <div className="bg-zinc-800 px-6 py-3 mb-2">
            <h3 className="text-xl font-bold text-white uppercase tracking-wide">VEHICLE INFO</h3>
          </div>
          <table className="w-full border border-zinc-300">
            <tbody>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100 w-1/3">YEAR</td>
                <td className="px-6 py-2">{booking.carYear}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">MAKE</td>
                <td className="px-6 py-2">{booking.carMake}</td>
              </tr>
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">MODEL</td>
                <td className="px-6 py-2">{booking.carModel}</td>
              </tr>
              {inspection.vehicleInfo.mileage && (
                <tr className="border-b border-zinc-300">
                  <td className="px-6 py-2 font-semibold bg-zinc-100">MILEAGE</td>
                  <td className="px-6 py-2">{inspection.vehicleInfo.mileage}</td>
                </tr>
              )}
              {inspection.vehicleInfo.transmission && (
                <tr className="border-b border-zinc-300">
                  <td className="px-6 py-2 font-semibold bg-zinc-100">TRANSMISSION</td>
                  <td className="px-6 py-2">{inspection.vehicleInfo.transmission}</td>
                </tr>
              )}
              <tr className="border-b border-zinc-300">
                <td className="px-6 py-2 font-semibold bg-zinc-100">VIN NUMBER</td>
                <td className="px-6 py-2">{booking.vin}</td>
              </tr>
              {inspection.vehicleInfo.interiorColor && (
                <tr className="border-b border-zinc-300">
                  <td className="px-6 py-2 font-semibold bg-zinc-100">INTERIOR COLOR</td>
                  <td className="px-6 py-2">{inspection.vehicleInfo.interiorColor}</td>
                </tr>
              )}
              {inspection.vehicleInfo.bodyColor && (
                <tr className="border-b border-zinc-300">
                  <td className="px-6 py-2 font-semibold bg-zinc-100">BODY COLOR</td>
                  <td className="px-6 py-2">{inspection.vehicleInfo.bodyColor}</td>
                </tr>
              )}
              {inspection.vehicleInfo.engine && (
                <tr className="border-b border-zinc-300">
                  <td className="px-6 py-2 font-semibold bg-zinc-100">ENGINE</td>
                  <td className="px-6 py-2">{inspection.vehicleInfo.engine}</td>
                </tr>
              )}
              {inspection.vehicleInfo.driveType && (
                <tr className="border-b border-zinc-300">
                  <td className="px-6 py-2 font-semibold bg-zinc-100">DRIVE TYPE</td>
                  <td className="px-6 py-2">{inspection.vehicleInfo.driveType}</td>
                </tr>
              )}
              {inspection.vehicleInfo.fuelType && (
                <tr className="border-b border-zinc-300">
                  <td className="px-6 py-2 font-semibold bg-zinc-100">FUEL TYPE</td>
                  <td className="px-6 py-2">{inspection.vehicleInfo.fuelType}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Inspection Sections */}
        {renderSection('EXTERIOR', inspection.exteriorChecks)}
        {renderSection('INTERIOR', inspection.interiorChecks)}
        {renderSection('MECHANICAL', inspection.mechanicalChecks)}
        {renderSection('ROAD TEST', inspection.roadTest)}

        {/* Inspector Notes */}
        {inspection.inspectorNotes && (
          <div className="mb-8 bg-zinc-100 p-6">
            <h2 className="text-2xl font-bold text-zinc-900 mb-4">INSPECTOR NOTES</h2>
            <p className="text-zinc-800 leading-relaxed" data-testid="inspector-notes">
              {inspection.inspectorNotes}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-6 border-t border-zinc-300">
          <p className="text-zinc-600">CarLens Inspections</p>
          <p className="text-zinc-500 text-sm">Professional Pre-Purchase Vehicle Inspections</p>
        </div>
      </div>
    </div>
  );
};

export default ViewReport;
